import { checkArithmetic } from "./exam.js";
import type { CurriculumStandard } from "./exam.js";
import type { Difficulty } from "./uasa.js";

/**
 * JomBelajar (formerly StudyBox) ready-made content. Written once per unit (topic) by AI, validated here, stored in the DB
 * and served instantly to students. Every lesson and question is tied to one DSKP Standard Pembelajaran.
 */
export type ContentLanguage = "ms" | "en" | "both";

export interface LessonContent {
  spCode: string;
  emoji: string;
  titleMs: string;
  titleEn: string;
  /** One or two plain sentences: what the child will learn. */
  summaryMs: string;
  summaryEn: string;
  /** 2–5 short notes (the "nota"). */
  pointsMs: string[];
  pointsEn: string[];
  /** A worked example or everyday illustration. */
  exampleMs: string;
  exampleEn: string;
  /** English description of one child-friendly illustration of this note (no text in the picture). Also shown with the quiz of this SP. */
  imagePrompt?: string;
}

export interface QuizItem {
  spCode: string;
  difficulty: Difficulty;
  stemMs: string;
  stemEn: string;
  optionsMs: string[];
  optionsEn: string[];
  /** Index 0–3 of the correct option. */
  answer: number;
  explanationMs: string;
  explanationEn: string;
  /** Maths only: arithmetic expression equal to the correct option. */
  check?: string;
}

export interface TopicContent {
  unitTitleEn: string;
  emoji: string;
  lessons: LessonContent[];
  questions: QuizItem[];
}

export interface ContentSpec {
  subjectName: string;
  level: "rendah" | "menengah";
  year: number;
  unitTitle: string;
  theme: string | null;
  standards: CurriculumStandard[];
  language: ContentLanguage;
  numeric: boolean;
  questionsPerSp: number;
  /** Subject slug (pendidikan_islam, bahasa_arab, …): adds KSSR guidance specific to that subject. */
  subject?: string;
}

export const CONTENT_LIMITS = { QUESTIONS_PER_SP: 4, QUIZ_LENGTH: 10, MAX_TEXT: 600 } as const;

/** Monolingual subjects (BM, BI): copy the primary language into the other fields so the UI never shows blanks. */
export function normaliseTopicContent(raw: TopicContent, language: ContentLanguage): TopicContent {
  // Models sometimes return numbers ("options": [24, 42, …]) or nulls; make every text field a string.
  const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));
  const arr = (v: unknown) => (Array.isArray(v) ? v.map(str) : []);
  const c: TopicContent = {
    unitTitleEn: str(raw?.unitTitleEn),
    emoji: str(raw?.emoji),
    lessons: (Array.isArray(raw?.lessons) ? raw.lessons : []).map((l) => ({
      spCode: str(l?.spCode), emoji: str(l?.emoji), titleMs: str(l?.titleMs), titleEn: str(l?.titleEn),
      summaryMs: str(l?.summaryMs), summaryEn: str(l?.summaryEn), pointsMs: arr(l?.pointsMs), pointsEn: arr(l?.pointsEn),
      exampleMs: str(l?.exampleMs), exampleEn: str(l?.exampleEn),
      ...(str(l?.imagePrompt).trim() ? { imagePrompt: str(l.imagePrompt).trim().slice(0, 300) } : {}),
    })),
    questions: (Array.isArray(raw?.questions) ? raw.questions : []).map((q) => ({
      spCode: str(q?.spCode), difficulty: q?.difficulty, stemMs: str(q?.stemMs), stemEn: str(q?.stemEn),
      optionsMs: arr(q?.optionsMs), optionsEn: arr(q?.optionsEn), answer: Number(q?.answer),
      explanationMs: str(q?.explanationMs), explanationEn: str(q?.explanationEn),
      ...(q?.check !== undefined && q?.check !== null && q?.check !== "" ? { check: str(q.check) } : {}),
    })),
  };
  if (language === "both") return c;
  const fromMs = language === "ms";
  const pick = (ms: string, en: string) => (fromMs ? ms : en);
  return {
    ...c,
    lessons: c.lessons.map((l) => ({
      ...l,
      titleMs: pick(l.titleMs, l.titleEn), titleEn: pick(l.titleMs, l.titleEn),
      summaryMs: pick(l.summaryMs, l.summaryEn), summaryEn: pick(l.summaryMs, l.summaryEn),
      pointsMs: fromMs ? l.pointsMs : l.pointsEn, pointsEn: fromMs ? l.pointsMs : l.pointsEn,
      exampleMs: pick(l.exampleMs, l.exampleEn), exampleEn: pick(l.exampleMs, l.exampleEn),
    })),
    questions: c.questions.map((q) => ({
      ...q,
      stemMs: pick(q.stemMs, q.stemEn), stemEn: pick(q.stemMs, q.stemEn),
      optionsMs: fromMs ? q.optionsMs : q.optionsEn, optionsEn: fromMs ? q.optionsMs : q.optionsEn,
      explanationMs: pick(q.explanationMs, q.explanationEn), explanationEn: pick(q.explanationMs, q.explanationEn),
    })),
  };
}

const blank = (s: unknown) => typeof s !== "string" || s.trim() === "";
const tooLong = (s: unknown) => typeof s === "string" && s.length > CONTENT_LIMITS.MAX_TEXT;

/** Validate AI output for one unit. Returns human-readable issues (empty = accept). */
export function validateTopicContent(c: TopicContent, spec: ContentSpec): string[] {
  const issues: string[] = [];
  const codes = new Set(spec.standards.map((s) => s.spCode));
  const needMs = spec.language !== "en";
  const needEn = spec.language !== "ms";
  const text = (where: string, ms: unknown, en: unknown) => {
    if (needMs && blank(ms)) issues.push(`${where}: teks BM kosong`);
    if (needEn && blank(en)) issues.push(`${where}: English text missing`);
    if (tooLong(ms) || tooLong(en)) issues.push(`${where}: terlalu panjang`);
  };

  if (!Array.isArray(c?.lessons) || !Array.isArray(c?.questions)) return ["format JSON tidak sah: perlu lessons[] dan questions[]"];

  for (const code of codes) {
    const n = c.lessons.filter((l) => l.spCode === code).length;
    if (n !== 1) issues.push(`SP ${code}: perlu tepat 1 nota (dapat ${n})`);
    const q = c.questions.filter((x) => x.spCode === code).length;
    if (q < spec.questionsPerSp) issues.push(`SP ${code}: perlu ${spec.questionsPerSp} soalan (dapat ${q})`);
  }
  for (const l of c.lessons) {
    if (!codes.has(l.spCode)) { issues.push(`nota untuk SP ${l.spCode} yang tiada dalam unit ini`); continue; }
    const w = `Nota ${l.spCode}`;
    text(`${w} tajuk`, l.titleMs, l.titleEn);
    text(`${w} ringkasan`, l.summaryMs, l.summaryEn);
    text(`${w} contoh`, l.exampleMs, l.exampleEn);
    const pts = (needMs ? l.pointsMs : l.pointsEn) ?? [];
    if (!Array.isArray(pts) || pts.length < 2 || pts.length > 5 || pts.some(blank)) issues.push(`${w}: perlu 2–5 isi penting`);
    if (needMs && needEn && (l.pointsMs?.length ?? 0) !== (l.pointsEn?.length ?? 0)) issues.push(`${w}: bilangan isi BM dan English tidak sama`);
  }
  c.questions.forEach((q, i) => {
    const w = `Soalan ${i + 1} (SP ${q.spCode})`;
    if (!codes.has(q.spCode)) { issues.push(`${w}: SP tiada dalam unit ini`); return; }
    if (!["rendah", "sederhana", "tinggi"].includes(q.difficulty)) issues.push(`${w}: aras tidak sah`);
    text(`${w} soalan`, q.stemMs, q.stemEn);
    text(`${w} penerangan`, q.explanationMs, q.explanationEn);
    for (const [lang, opts, need] of [["BM", q.optionsMs, needMs], ["EN", q.optionsEn, needEn]] as const) {
      if (!need) continue;
      if (!Array.isArray(opts) || opts.length !== 4 || opts.some(blank)) { issues.push(`${w}: perlu 4 pilihan ${lang}`); continue; }
      // Exact compare: capitalisation/punctuation questions legitimately differ only in case or a full stop.
      if (new Set(opts.map((o) => o.trim())).size !== 4) issues.push(`${w}: pilihan ${lang} berulang`);
    }
    if (!Number.isInteger(q.answer) || q.answer < 0 || q.answer > 3) { issues.push(`${w}: answer mesti 0–3`); return; }
    if (q.check) {
      const opt = (needMs ? q.optionsMs : q.optionsEn)?.[q.answer] ?? "";
      const r = checkArithmetic(q.check, opt);
      if (!r.ok) issues.push(`${w}: ${r.reason}`);
    }
  });
  return issues;
}

/** KSSR guidance that only applies to one subject (religion, language, art and technology need care the generic prompt cannot give). */
export function subjectGuidance(subject?: string): string {
  switch (subject) {
    case "pendidikan_islam":
      return "Pendidikan Islam KSSR (Al-Quran, Akidah, Ibadah, Sirah, Adab, Jawi). JANGAN mereka ayat al-Quran, hadis atau doa: petik hanya bacaan pendek yang pasti betul dan biasa dihafal murid (Al-Fatihah, Al-Ikhlas, An-Nas, Al-Falaq, doa harian lazim) dalam tulisan Arab berbaris tepat, dengan maksud BM. Sebut Allah SWT dan Nabi Muhammad SAW dengan adab. Dalam imagePrompt JANGAN gambarkan Allah, nabi, rasul, malaikat atau sahabat; guna murid, masjid, alatan solat, alam.";
    case "pendidikan_moral":
      return "Pendidikan Moral KSSR: nilai murni (kasih sayang, hormat-menghormati, baik hati, adil, bertanggungjawab, berterima kasih, kejujuran, kerjasama). Beri situasi murid di rumah, sekolah dan komuniti; neutral terhadap agama dan kaum; soalan menilai pilihan tingkah laku yang baik beserta sebabnya.";
    case "bahasa_arab":
      return "Bahasa Arab KSSR (peringkat asas): tulis setiap perkataan dan ayat Arab dalam tulisan Arab BERBARIS lengkap, diikuti sebutan rumi dan maksud dalam Bahasa Melayu. Guna perkataan asas yang betul (keluarga, sekolah, warna, nombor, anggota badan, makanan, kata sapaan). Soalan boleh meminta padanan perkataan Arab dengan maksud; pilihan jawapan boleh dalam tulisan Arab. Pastikan ejaan Arab betul.";
    case "pendidikan_seni_visual":
      return "Pendidikan Seni Visual KSSR: unsur seni (garisan, bentuk, warna, jalinan, ruang), prinsip reka bentuk, seni halus, catan, kolaj, cetakan, seni tampak tempatan. Soalan menguji pemerhatian, istilah dan pilihan teknik, bukan kerja melukis.";
    case "reka_bentuk_teknologi":
      return "Reka Bentuk dan Teknologi KSSR: proses reka bentuk, bahan, alat tangan dan keselamatan, mekanikal, elektrik, makanan, pengaturcaraan asas, kerjaya teknologi. Contoh mesra murid dan selamat.";
    default:
      return "";
  }
}

/** Prompt for one unit. The model writes notes + a question bank for every SP in the unit. */
export function buildContentPrompt(spec: ContentSpec, feedback?: string): { system: string; user: string } {
  const lvl = spec.level === "menengah" ? `Tingkatan ${spec.year}` : `Tahun ${spec.year}`;
  const age = (spec.level === "menengah" ? 12 : 6) + spec.year;
  const lang =
    spec.language === "both"
      ? "Tulis setiap medan dalam DUA bahasa: medan *Ms dalam Bahasa Melayu baku, medan *En terjemahan English yang setara (istilah DLP)."
      : spec.language === "en"
        ? "This is an English Language subject: write every *En field in simple English; leave every *Ms field as an empty string or array."
        : "Tulis setiap medan *Ms dalam Bahasa Melayu baku; biarkan medan *En kosong.";
  const system = [
    `Anda guru ${spec.subjectName} yang mesra dan berpengalaman di Malaysia, menulis bahan JomBelajar untuk murid ${lvl} (kira-kira ${age} tahun) yang belajar sendiri.`,
    "Setiap nota dan soalan MESTI berdasarkan huraian Standard Pembelajaran (SP) yang diberi sahaja. Jangan tambah SP lain atau kod baharu.",
    "Nota: tajuk mesra, ringkasan 1–2 ayat, 2–5 isi penting yang pendek dan jelas, satu contoh daripada kehidupan seharian murid Malaysia.",
    `Soalan: tepat ${spec.questionsPerSp} soalan objektif untuk SETIAP SP (2 rendah, 1 sederhana, 1 tinggi/KBAT). 4 pilihan, satu sahaja betul, pengganggu munasabah, "answer" ialah indeks 0–3. Penerangan ringkas kenapa jawapan itu betul. Jangan letak huruf A/B/C/D dalam pilihan.`,
    spec.numeric
      ? "Untuk soalan yang jawapannya nombor, sertakan \"check\": ungkapan aritmetik (hanya digit, + - * / ( ) dan titik perpuluhan) yang nilainya sama dengan pilihan yang betul."
      : "Jangan sertakan medan \"check\".",
    "Bahasa mudah, ayat pendek, sesuai umur. Konteks Malaysia (nama tempatan, RM, unit metrik). Tiada kandungan sensitif. Cipta bahan asli; jangan salin buku teks.",
    "Pilih satu emoji yang sesuai untuk unit dan untuk setiap nota.",
    "Untuk setiap nota tulis juga \"imagePrompt\": huraian satu adegan dalam BAHASA INGGERIS (maksimum 200 aksara) untuk ilustrasi kartun mesra kanak-kanak yang menunjukkan konsep nota itu dengan jelas (objek dan tindakan yang boleh dilukis). Tiada tulisan, huruf atau nombor dalam gambar; tiada tokoh sebenar.",
    subjectGuidance(spec.subject),
    lang,
    "Balas dengan JSON SAHAJA, tanpa teks lain.",
  ].filter(Boolean).join("\n");
  const user = JSON.stringify({
    unit: { tajuk: spec.unitTitle, tema: spec.theme, tahap: lvl },
    standard_pembelajaran: spec.standards.map((s) => ({ sp: s.spCode, huraian: s.spDesc, sk: s.skCode, huraian_sk: s.skDesc })),
    format_jawapan: {
      unitTitleEn: "English unit title",
      emoji: "🔬",
      lessons: [{ spCode: "1.1.1", emoji: "👀", titleMs: "", titleEn: "", summaryMs: "", summaryEn: "", pointsMs: ["", ""], pointsEn: ["", ""], exampleMs: "", exampleEn: "", imagePrompt: "a smiling boy looking at a plant through a magnifying glass in a garden" }],
      questions: [{ spCode: "1.1.1", difficulty: "rendah", stemMs: "", stemEn: "", optionsMs: ["", "", "", ""], optionsEn: ["", "", "", ""], answer: 2, explanationMs: "", explanationEn: "" }],
    },
    ...(feedback ? { pembetulan: `Percubaan lepas ditolak kerana: ${feedback}. Betulkan semua isu ini dan pulangkan JSON lengkap.` } : {}),
  });
  return { system, user };
}

/** Pull the JSON object out of a model reply (tolerates code fences or stray text). */
export function parseTopicContent(text: string): TopicContent | null {
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start < 0 || end <= start) return null;
  try {
    return JSON.parse(text.slice(start, end + 1)) as TopicContent;
  } catch {
    return null;
  }
}

/** Stars for a quiz result: 3 = ≥90 %, 2 = ≥70 %, 1 = ≥50 %. */
export function quizStars(score: number, max: number): 0 | 1 | 2 | 3 {
  if (max <= 0) return 0;
  const p = score / max;
  return p >= 0.9 ? 3 : p >= 0.7 ? 2 : p >= 0.5 ? 1 : 0;
}

/**
 * Keep every lesson and question that passes validation on its own, and report which SPs still lack
 * a lesson or enough questions. Lets the writer re-ask only for the gaps instead of discarding a
 * whole reply because one distractor was duplicated.
 */
export function salvageTopicContent(c: TopicContent, spec: ContentSpec, minQuestionsPerSp: number): {
  content: TopicContent;
  issues: string[];
  incomplete: string[];
} {
  const issues: string[] = [];
  const kept: TopicContent = { unitTitleEn: c?.unitTitleEn ?? "", emoji: c?.emoji ?? "", lessons: [], questions: [] };
  const bySp = new Map(spec.standards.map((s) => [s.spCode, s]));
  const one = (sp: string): ContentSpec => ({ ...spec, standards: [bySp.get(sp)!], questionsPerSp: 0 });
  for (const l of Array.isArray(c?.lessons) ? c.lessons : []) {
    if (!bySp.has(l.spCode) || kept.lessons.some((x) => x.spCode === l.spCode)) continue;
    const errs = validateTopicContent({ ...kept, lessons: [l], questions: [] }, one(l.spCode)).filter((e) => !e.includes("perlu tepat 1 nota"));
    if (errs.length) issues.push(...errs); else kept.lessons.push(l);
  }
  for (const q of Array.isArray(c?.questions) ? c.questions : []) {
    if (!bySp.has(q.spCode)) continue;
    const errs = validateTopicContent({ ...kept, lessons: [], questions: [q] }, one(q.spCode)).filter((e) => !e.includes("perlu tepat 1 nota"));
    if (errs.length) issues.push(...errs); else kept.questions.push(q);
  }
  const incomplete = spec.standards
    .map((s) => s.spCode)
    .filter((sp) => !kept.lessons.some((l) => l.spCode === sp) || kept.questions.filter((q) => q.spCode === sp).length < minQuestionsPerSp);
  return { content: kept, issues, incomplete };
}
